import React, { useState, useEffect } from "react";
import "../AdDetailModal.css";
import {
  updateKeywordAPI,
  patchAdExtensionAPI,
  deleteKeywordAPI,
} from "../../../../../api";

const SimpleKeywordEditModal = ({
  isOpen,
  onClose,
  adData,
  keywordData,
  onSave,
}) => {
  const [keywordRanks, setKeywordRanks] = useState([]);

  // 모달이 열릴 때 키워드 데이터 초기화
  useEffect(() => {
    if (isOpen && keywordData) {
      // keywordData를 keywordRanks 형식으로 변환 (MAIN 타입 제외)
      const initialKeywordRanks = keywordData
        .filter((keyword) => keyword.type !== "MAIN") // MAIN 타입 제외
        .map((keyword, index) => ({
          keyword: keyword.name || "",
          selected: true, // 기존 키워드는 모두 선택된 상태
          type: keyword.type || "SUB",
          isExisting: true, // 기존 키워드 표시
          keyword_id: keyword.keyword_id, // 키워드 ID 추가
        }));
      setKeywordRanks(initialKeywordRanks);
    }
  }, [isOpen, keywordData]);

  // 키워드 추가 함수
  const addKeywordRank = () => {
    setKeywordRanks((prev) => [
      ...prev,
      {
        keyword: "",
        selected: true,
        type: "SUB",
        isExisting: false, // 새로 추가된 키워드 표시
      },
    ]);
  };

  // 키워드 제거 함수
  const removeKeywordRank = (index) => {
    setKeywordRanks((prev) => {
      return prev.map((item, i) =>
        i === index ? { ...item, selected: false } : item
      );
    });
  };

  // 키워드 업데이트 함수
  const updateKeywordRank = (index, field, value) => {
    setKeywordRanks((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item))
    );
  };

  // 수정 버튼 클릭 처리
  const handleSave = async () => {
    // 선택된 키워드들 중 keyword가 빈 문자열이 아닌 것들만 필터링
    const selectedKeywords = keywordRanks.filter(
      (item) => item.selected && item.keyword && item.keyword.trim() !== ""
    );

    // 최소 1개, 최대 6개 검증
    if (selectedKeywords.length === 0) {
      alert("최소 1개의 키워드를 선택해주세요.");
      return;
    }

    if (selectedKeywords.length > 10) {
      alert("최대 6개까지만 선택할 수 있습니다.");
      return;
    }

    // 원래 기존 키워드들과 비교
    const originalKeywords = keywordData || [];

    // 기존 키워드와 새로운 키워드 구분
    const existingKeywords = selectedKeywords.filter((item) => item.isExisting);
    const newKeywords = selectedKeywords.filter((item) => !item.isExisting);

    // 기존 키워드들과 비교
    if (newKeywords.length > 0) {
      // 선택되지 않은 기존 키워드들 찾기
      const unselectedExistingKeywords = keywordRanks.filter(
        (item) => !item.selected && item.isExisting
      );

      // 새로 추가된 키워드들을 처리
      const keywordsToProcess = [...newKeywords];

      // 1. 선택되지 않은 기존 키워드들에 새 키워드들을 업데이트
      for (
        let i = 0;
        i <
        Math.min(unselectedExistingKeywords.length, keywordsToProcess.length);
        i++
      ) {
        const existingKeyword = unselectedExistingKeywords[i];
        const newKeyword = keywordsToProcess[i];

        try {
          await updateKeywordAPI(
            existingKeyword.keyword_id,
            newKeyword.keyword
          );
        } catch (error) {
          console.error("키워드 업데이트 실패:", error);
        }
      }

      // 2. 남은 새 키워드들을 extension에 추가
      const remainingNewKeywords = keywordsToProcess.slice(
        unselectedExistingKeywords.length
      );

      if (remainingNewKeywords.length > 0) {
        const extensionData = {
          ad_id: adData.ad_id || adData.id,
          subKeywords: remainingNewKeywords.map((keyword) => ({
            name: keyword.keyword,
          })),
        };

        try {
          await patchAdExtensionAPI(extensionData);
        } catch (error) {
          console.error("Extension 키워드 추가 실패:", error);
        }
      }

      // 3. 남은 기존 키워드들을 삭제
      const remainingExistingKeywords = unselectedExistingKeywords.slice(
        keywordsToProcess.length
      );

      if (remainingExistingKeywords.length > 0) {
        for (const existingKeyword of remainingExistingKeywords) {
          try {
            await deleteKeywordAPI(
              parseInt(existingKeyword.keyword_id) ||
                Number(existingKeyword.keyword_id)
            );
          } catch (error) {
            console.error("키워드 삭제 실패:", error);
          }
        }
      }
    } else {
      // 선택되지 않은 기존 키워드들이 있으면 삭제
      const unselectedExistingKeywords = keywordRanks.filter(
        (item) => !item.selected && item.isExisting
      );

      if (unselectedExistingKeywords.length > 0) {
        for (const existingKeyword of unselectedExistingKeywords) {
          try {
            await deleteKeywordAPI(
              parseInt(existingKeyword.keyword_id) ||
                Number(existingKeyword.keyword_id)
            );
          } catch (error) {
            console.error("키워드 삭제 실패:", error);
          }
        }
      }
    }

    // onSave 콜백 호출
    onSave(selectedKeywords);

    // 팝업 닫기
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="ad-detail-modal-overlay">
      <div className="ad-detail-modal" onClick={(e) => e.stopPropagation()}>
        {/* 헤더 */}
        <div className="modal-header">
          <h2>키워드 수정</h2>
        </div>

        {/* 내용 */}
        <div className="modal-content">
          <div className="my-ad-keyword-edit-section">
            <div className="my-ad-keyword-inputs-grid">
              {keywordRanks
                .filter((item) => item.selected)
                .map((item, index) => (
                  <div key={index} className="my-ad-keyword-input-item">
                    <div className="my-ad-label-container">
                      <label>
                        {index === 0 ? "*" : ""}
                        유입용 키워드{index + 1}
                        {index === 0 ? "(필수)" : "(선택)"}
                      </label>
                      <button
                        type="button"
                        className="my-ad-remove-keyword-btn"
                        onClick={() => removeKeywordRank(index)}
                      >
                        ×
                      </button>
                    </div>
                    <input
                      type="text"
                      placeholder="키워드 입력"
                      value={item.keyword || ""}
                      onChange={(e) =>
                        updateKeywordRank(index, "keyword", e.target.value)
                      }
                      className="my-ad-keyword-input-field"
                      readOnly={item.isExisting} // 기존 키워드는 수정 불가
                    />
                  </div>
                ))}

              {keywordRanks.filter((item) => item.selected).length < 10 && (
                <button
                  className="my-ad-add-keyword-btn"
                  onClick={addKeywordRank}
                >
                  +
                </button>
              )}
            </div>
          </div>
        </div>

        {/* 푸터 */}
        <div className="modal-footer">
          <button className="ad-detail-edit-keyword-btn" onClick={handleSave}>
            수정
          </button>
          <button className="ad-detail-close-btn" onClick={onClose}>
            닫기
          </button>
        </div>
      </div>
    </div>
  );
};

export default SimpleKeywordEditModal;
